import { type UseQueryResult, useQueries } from "@tanstack/react-query"
import type { Omit } from "@wagmi/core/internal"
import { hashFn } from "@wagmi/core/query"
import { useMemo } from "react"
import { Evaluate, zeroAddress } from "viem"
import { type Config, useChainId, useConfig } from "wagmi"
import {
  GetManagedVaultErrorType,
  getManagedVault,
} from "../actions/getManagedVault"
import {
  GetManagedVaultData,
  GetManagedVaultOptions,
  GetManagedVaultQueryFnData,
  GetManagedVaultQueryKey,
  getManagedVaultQueryKey,
} from "../query"
import type {
  ConfigParameter,
  ExtendedConfig,
  UseQueryParameters,
} from "../types"

export type UseManagedVaultsProps<
  config extends Config = Config,
  selectedData = GetManagedVaultData,
  combineData = UseQueryResult<selectedData>[],
> = ConfigParameter<config> & {
  queries: readonly (GetManagedVaultOptions & {
    query?:
      | Evaluate<
          Omit<
            UseQueryParameters<
              GetManagedVaultQueryFnData,
              GetManagedVaultErrorType,
              GetManagedVaultData,
              GetManagedVaultQueryKey
            >,
            "gcTime" | "staleTime" | "select"
          >
        >
      | undefined
  })[]
  select?: (data: GetManagedVaultData) => selectedData
  combine?: (result: UseQueryResult<selectedData>[]) => combineData
}

export function useManagedVaults<
  config extends ExtendedConfig,
  selectedData = GetManagedVaultData,
  combineData = UseQueryResult<selectedData>[],
>(
  parameters: UseManagedVaultsProps<config, selectedData, combineData>,
): combineData {
  const { queries = [], select, combine } = parameters
  const config = useConfig(parameters)
  const chainId = useChainId({ config })
  const queryResults = useQueries({
    queries: queries.map(({ scopeKey, query = {}, ...vault }) => ({
      ...query,
      queryKey: getManagedVaultQueryKey({
        address: vault.address,
        chainId: vault.chainId || chainId,
        evcAddress: vault.evcAddress,
        eulerEarnVaultLensAddress: vault.eulerEarnVaultLensAddress,
        scopeKey,
      }),
      retry: false,
      refetchOnMount: false,
      queryKeyHashFn: hashFn,
      queryFn: () =>
        getManagedVault(config, {
          ...vault,
          chainId: vault.chainId || chainId,
        }),
      select: select,
      enabled: Boolean(
        vault.address &&
          vault.address !== zeroAddress &&
          (query.enabled ?? true),
      ),
    })),
  }) satisfies UseQueryResult<selectedData>[]

  return useMemo(
    () =>
      combine
        ? combine(queryResults)
        : (queryResults as unknown as combineData),
    [queryResults, combine],
  )
}
